/**
 * Dev settings composable for localhost testing
 * These settings are stored in localStorage and only affect local development
 */
import { ref, watch, computed } from "vue";

const STORAGE_KEY = "ai-assistant-dev-settings";

export interface DevSettings {
  // Recovery mode testing
  simulateSocketDisconnect: boolean;
  simulatePollingTimeout: boolean;
  // Additional dev settings
  enableVerboseLogging: boolean;
  showDevIndicators: boolean;
  // State machine feature flag
  useNewStateMachine: boolean;
}

export interface TestPreset {
  id: string;
  name: string;
  description: string;
  icon: string;
  settings: Partial<DevSettings>;
}

const defaultSettings: DevSettings = {
  simulateSocketDisconnect: false,
  simulatePollingTimeout: false,
  enableVerboseLogging: true,
  showDevIndicators: true,
  useNewStateMachine: true, // Default to new state machine
};

// Test presets for quick scenario switching
export const TEST_PRESETS: TestPreset[] = [
  {
    id: "clean",
    name: "Clean Slate",
    description: "All simulations off, normal operation",
    icon: "✨",
    settings: {
      simulateSocketDisconnect: false,
      simulatePollingTimeout: false,
    },
  },
  {
    id: "recovery",
    name: "Recovery Flow",
    description: "Test socket disconnect recovery mechanism",
    icon: "🔄",
    settings: {
      simulateSocketDisconnect: true,
      simulatePollingTimeout: false,
    },
  },
  {
    id: "timeout",
    name: "Timeout Test",
    description: "Test polling timeout and refresh fallback",
    icon: "⏱️",
    settings: {
      simulateSocketDisconnect: true,
      simulatePollingTimeout: true,
    },
  },
  {
    id: "legacy-state",
    name: "Legacy State Machine",
    description: "Use old state management (useAIAssistantState)",
    icon: "📦",
    settings: {
      useNewStateMachine: false,
    },
  },
];

// Load settings from localStorage
function loadSettings(): DevSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultSettings, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.warn("[DevSettings] Failed to load from localStorage:", error);
  }
  return { ...defaultSettings };
}

// Save settings to localStorage
function saveSettings(settings: DevSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn("[DevSettings] Failed to save to localStorage:", error);
  }
}

// Singleton state
const settings = ref<DevSettings>(loadSettings());

// Watch for changes and persist
watch(
  settings,
  (newSettings) => {
    saveSettings(newSettings);
  },
  { deep: true }
);

export function useDevSettings() {
  // Computed: count of active test scenarios
  const activeScenarioCount = computed(() => {
    let count = 0;
    if (settings.value.simulateSocketDisconnect) count++;
    if (settings.value.simulatePollingTimeout) count++;
    return count;
  });

  // Computed: get current active preset (if any matches exactly)
  const activePreset = computed(() => {
    return TEST_PRESETS.find((preset) => {
      return Object.entries(preset.settings).every(([key, value]) => {
        return settings.value[key as keyof DevSettings] === value;
      });
    });
  });

  const updateSetting = <K extends keyof DevSettings>(key: K, value: DevSettings[K]) => {
    settings.value[key] = value;
  };

  const applyPreset = (presetId: string) => {
    const preset = TEST_PRESETS.find((p) => p.id === presetId);
    if (preset) {
      Object.entries(preset.settings).forEach(([key, value]) => {
        (settings.value as Record<string, unknown>)[key] = value;
      });
    }
  };

  const resetSettings = () => {
    settings.value = { ...defaultSettings };
  };

  const exportSettings = () => {
    return JSON.stringify(settings.value, null, 2);
  };

  const importSettings = (json: string) => {
    try {
      const imported = JSON.parse(json);
      settings.value = { ...defaultSettings, ...imported };
      return true;
    } catch {
      return false;
    }
  };

  return {
    settings,
    activeScenarioCount,
    activePreset,
    updateSetting,
    applyPreset,
    resetSettings,
    exportSettings,
    importSettings,
    presets: TEST_PRESETS,
  };
}

// Helper functions for components to check settings without importing the full composable
export function shouldSimulateSocketDisconnect(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.simulateSocketDisconnect === true;
    }
  } catch {
    // Ignore errors
  }
  return false;
}

export function shouldSimulatePollingTimeout(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.simulatePollingTimeout === true;
    }
  } catch {
    // Ignore errors
  }
  return false;
}

export function isVerboseLoggingEnabled(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.enableVerboseLogging !== false; // Default to true
    }
  } catch {
    // Ignore errors
  }
  return true;
}

export function useNewStateMachine(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.useNewStateMachine !== false; // Default to true (new state machine)
    }
  } catch {
    // Ignore errors
  }
  return true;
}
